import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import axios from "axios"

import { 
    Pagination, 
    Container,
    Box,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
    Grid,
} from "@mui/material"
import SearchIcon from '@mui/icons-material/Search'

import UserContextP from "Interfaces/Context"
import { useAuth } from "Context/Auth/useAuth"

// Componentes
import Loader from "Components/Loader"
import SearchField from "Components/SearchField"
import ErrorPage from "Components/NotAccesible/ErrorPage"
import ItemOrder from "Components/Document/ItemOrder"

const InformationRequest = () => {
    const { t } = useTranslation()
    const context = useContext(UserContextP)
    const auth = useAuth()
    const theme = useTheme()
    const isLarge = useMediaQuery(theme.breakpoints.up('md'))
    const [data, setData] = useState<any>([])
    const [copyData, setCopyData] = useState<any>([])
    const [empty, setEmpty] = useState(false)
    const [search, setSearch] = useState<string>('')
    const [loader, setLoader] = useState<boolean>(true)
    const [error, setError] = useState<boolean>(false)
    const [pagination, setPagination] = useState(1)


    useEffect(() => {
        if (context.service?.router?.POST?.get_inventory && auth.auth.token !== "" ) getDataInitial()
        // eslint-disable-next-line
    }, [ context.service?.router?.POST?.get_inventory, auth.auth.token ])

    const getDataInitial = async () => {
        let body = {
            data: {
                priceList: auth.user?.priceList ?? 0
            }
        }
        await axios.post(context.service?.router?.POST?.get_inventory, body, {
            headers: {
                'Authorization': `UserToken ${auth.auth?.token}`
            }
        }).then((response) => {
            setData(response.data.data)
            setCopyData(response.data.data)
            setLoader(false)
        }).catch((error) => {
            setLoader(false)
            setError(true)
           
        })
    }
    //Le coloca una paginacion a la ventana
    const changePagination = (event: any, value: any) => {
        setPagination(value)
    }

    return (
        <>
            {
                loader ?
                    <Loader
                        inPage={true}
                        openLoader={loader}
                        redirect={false}
                        redirectHome={false}
                        quantity={0}
                    />
                    :
                    error ?
                        <ErrorPage message={t("benchmarks.text_1")} />
                        :
                        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
                            {/* Header Section */}
                            <Box sx={{ mb: 4 }}>
                                <Typography 
                                    variant="h4" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        color: "#173456",
                                        mb: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <SearchIcon sx={{ fontSize: 28, color: "#173456" }} />
                                    {t("consulta de informacion.text_2")}
                                </Typography>
                            </Box>

                            {/* Search Section */}
                            <Paper 
                                elevation={2}
                                sx={{ 
                                    // p: 3, 
                                    mb: 4, 
                                    borderTop: "4px solid #173456",
                                    borderRadius: "12px",
                                }}
                            >
                                <SearchField
                                    text={search}
                                    setText={setSearch}
                                    placeholder={t("consulta de informacion.text_2")}
                                    size="medium"
                                    useFilters={false}
                                    data={copyData}
                                    result={data}
                                    empty={empty}
                                    setResult={setData}
                                    setEmpty={setEmpty}
                                    fullWidth={true}
                                    setPagination={setPagination} 
                                />
                            </Paper>

                            {/* Content Section */}
                            {data.length === 0 ? (
                                <Paper 
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        textAlign: "center",
                                        backgroundColor: "#f5f5f5",
                                        borderRadius: "12px",
                                        border: "2px dashed #ddd",
                                    }}
                                >
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            color: "#999",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {t("consulta de informacion.text_3")}
                                    </Typography>
                                </Paper>
                            ) : (
                                <>
                                    {/* Results Info */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                color: "#666",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Se encontraron <strong style={{ color: "#30B595" }}>{data.length}</strong> resultado{data.length !== 1 ? 's' : ''} • Página <strong>{pagination}</strong> de <strong>{Math.ceil(data.length / 21)}</strong>
                                        </Typography>
                                    </Box>

                                    {/* Items List */}
                                    <Grid
                                        container
                                        spacing={2}
                                        sx={{
                                            mb: 4,
                                        }}
                                    >
                                        {
                                            data?.map((item: any, index: any) => {
                                                return (
                                                    index >= (21 * pagination - 21) && index < (21 * pagination) ?
                                                        <ItemOrder 
                                                            key={index}
                                                            item={item} 
                                                            productSelected={()=> {}} 
                                                            showCheckbox={false} 
                                                            showDetails={true} 
                                                            showPrice={false}
                                                        />
                                                        :
                                                        null
                                                )
                                            })
                                        }
                                    </Grid>

                                    {/* Pagination Section */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            backgroundColor: "#f9fafb",
                                            borderRadius: "12px",
                                            border: "1px solid #eee",
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Pagination 
                                            count={Math.ceil(data.length / 21)} 
                                            page={pagination}
                                            defaultPage={1} 
                                            siblingCount={isLarge ? 2 : 0}
                                            onChange={changePagination}
                                            sx={{
                                                "& .MuiPaginationItem-page.Mui-selected": {
                                                    backgroundColor: "#30B595",
                                                    color: "#fff",
                                                    fontWeight: 600,
                                                },
                                                "& .MuiPaginationItem-page:hover": {
                                                    backgroundColor: "rgba(48, 181, 149, 0.1)",
                                                },
                                            }}
                                        />
                                    </Paper>
                                </>
                            )}
                        </Container>
            }
        </>
    )
}

export default InformationRequest